// AI response generation using Google Gemini API

/**
 * Generate an email response using AI based on the thread and knowledge base
 * @param {Array} threadMessages - Array of messages in the email thread
 * @param {string|null} knowledgebase - Knowledge base content to inform the response
 * @param {Object} userSettings - User settings including signOffName and kbLink
 * @returns {Promise<string>} Generated response text
 */
export const generateAIResponse = async (threadMessages, knowledgebase, userSettings = {}) => {
  const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

  if (!GEMINI_API_KEY) {
    throw new Error('Gemini API key not configured. Please add REACT_APP_GEMINI_API_KEY to your .env file');
  }

  console.log('Knowledge base available:', !!knowledgebase);
  if (knowledgebase) {
    console.log('Knowledge base length:', knowledgebase.length);
    console.log('Knowledge base preview:', knowledgebase.substring(0, 200));
  }

  // Build the conversation context from the thread (limit to last 3 messages to save tokens)
  const recentMessages = threadMessages.slice(-3);
  const conversationContext = recentMessages.map((msg, index) => {
    const from = msg.payload?.headers?.find(h => h.name === 'From')?.value || 'Unknown';
    const subject = msg.payload?.headers?.find(h => h.name === 'Subject')?.value || 'No Subject';

    // Extract email body
    let body = extractEmailBody(msg);

    // Limit body length to avoid token overflow
    if (body.length > 1000) {
      body = body.substring(0, 1000) + '...';
    }

    return `Message ${index + 1}:
From: ${from}
Subject: ${subject}

${body}

---`;
  }).join('\n\n');

  // Limit knowledge base size
  let kbContent = knowledgebase;
  if (kbContent && kbContent.length > 3000) {
    kbContent = kbContent.substring(0, 3000) + '\n\n[Knowledge base truncated due to length]';
  }

  // Define personality-specific instructions
  const personalityPrompts = {
    professional: 'You are a professional and business-focused customer support assistant. Be clear, direct, and maintain a formal tone while being helpful.',
    friendly: 'You are a warm, friendly, and conversational customer support assistant. Be approachable, use casual language (while remaining professional), and make customers feel comfortable.',
    empathetic: 'You are a compassionate and understanding customer support assistant. Show deep empathy, acknowledge customer feelings, and provide supportive, caring responses.',
    enthusiastic: 'You are an energetic and positive customer support assistant. Be encouraging, use exclamation points appropriately, and convey excitement about helping customers.',
    concise: 'You are a brief and efficient customer support assistant. Get straight to the point, use short sentences, and provide only essential information without unnecessary elaboration.'
  };

  const personalityStyle = personalityPrompts[userSettings.personality] || personalityPrompts.professional;

  // Build the prompt
  const prompt = `${personalityStyle} Generate a response to the email thread below${kbContent ? ' using the knowledge base for context' : ''}.

${kbContent ? `KNOWLEDGE BASE:
${kbContent}

---

` : ''}EMAIL THREAD (most recent ${recentMessages.length} messages):
${conversationContext}

Generate a helpful response to the most recent email following this personality style. ALWAYS:
- Start with an appropriate greeting (e.g., "Hi [Name]," or "Hello,")
- Provide clear, helpful information
- Match the ${userSettings.personality} personality tone throughout
- End with an appropriate closing${userSettings.signOffName ? `\n- Sign off with: "Best regards,\\n${userSettings.signOffName}"` : ''}

Do not include additional signature - just the email body with greeting${userSettings.signOffName ? ' and sign-off' : ''}.`;

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 2048,
            responseModalities: ["TEXT"]
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_NONE"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_NONE"
            }
          ]
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Gemini API error: ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('Gemini API response:', data);

    const candidate = data.candidates?.[0];

    if (!candidate) {
      console.error('No candidates in response:', data);
      throw new Error('No response generated from AI');
    }

    // Extract text from parts, skipping thought parts
    const parts = candidate.content?.parts || [];
    let generatedText = '';

    for (const part of parts) {
      if (part.text) {
        generatedText += part.text;
      }
    }

    if (!generatedText.trim()) {
      console.error('No text found in response parts:', data);
      throw new Error(`No text generated. Finish reason: ${candidate.finishReason}`);
    }

    // Append knowledge base link if available
    let finalResponse = generatedText.trim();

    if (knowledgebase && userSettings.kbLink) {
      finalResponse += `\n\nFor more detailed information, please refer to our knowledge base: ${userSettings.kbLink}`;
    }

    return finalResponse;
  } catch (error) {
    console.error('AI generation error:', error);
    throw error;
  }
};

/**
 * Extract plain text body from email message
 */
const extractEmailBody = (email) => {
  const walkParts = (parts) => {
    for (const part of parts) {
      if (part.mimeType === 'text/plain' && part.body?.data) {
        return part.body.data;
      }
      if (part.mimeType === 'text/html' && part.body?.data) {
        return part.body.data;
      }
      if (part.parts) {
        const found = walkParts(part.parts);
        if (found) return found;
      }
    }
    return null;
  };

  const bodyData = walkParts(email.payload?.parts || []) || email.payload?.body?.data;

  if (!bodyData) return 'No content available.';

  try {
    // Decode base64url to UTF-8
    const base64 = bodyData.replace(/-/g, '+').replace(/_/g, '/');
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const decoder = new TextDecoder('utf-8');
    let decoded = decoder.decode(bytes);

    // Handle quoted-printable encoding
    decoded = decoded
      .replace(/=\r?\n/g, '')
      .replace(/=([0-9A-F]{2})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)));

    // Strip HTML tags if present (basic stripping for context)
    decoded = decoded.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    return decoded;
  } catch (e) {
    return 'Failed to decode content.';
  }
};
